import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { billingApi } from '../services/api';
import './BalanceBadge.css';

/**
 * Виджет баланса в шапке. Показывается только если у пользователя есть company_id
 * (он клиент или COMPANY_ADMIN/работник одной компании). Глобальный ADMIN
 * без company_id не видит — он сам не клиент.
 */
function BalanceBadge({ collapsed = false }) {
  const { user, isInternalStaff } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [error, setError] = useState(false);

  // Сальдо клиенту не показываем (аудит №122, решение владельца 25.09): оплаты
  // вносятся не все, красный минус в миллионы в шапке вводит в заблуждение.
  // Значок бывает только у пользователя с компанией, то есть у клиента, —
  // поэтому по сути он выключен; складу баланс виден в «Финансах».
  const companyId = isInternalStaff ? user?.company_id : null;

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    let t = null;
    const load = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;   // вкладка в фоне
      try {
        const res = await billingApi.getBalance(companyId);
        if (!cancelled) {
          setBalance(res.data);
          setError(false);
        }
      } catch (e) {
        if (cancelled) return;
        setError(true);
        // 401/403 — дальше опрашивать бессмысленно (аудит №3, E13)
        const st = e && e.response && e.response.status;
        if ((st === 401 || st === 403) && t) { clearInterval(t); t = null; }
      }
    };
    load();
    // обновляем раз в минуту
    t = setInterval(load, 60_000);
    return () => { cancelled = true; if (t) clearInterval(t); };
  }, [companyId]);

  if (!companyId || error || !balance) return null;

  const value = Number(balance.balance) || 0;
  let level = 'pos';
  if (value <= 0) level = 'neg';
  else if (value <= 1000) level = 'warn';

  const formatted = value.toLocaleString('ru-RU', { timeZone: 'Europe/Moscow', minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (collapsed) {
    return (
      <button
        className={`balance-badge balance-badge--collapsed balance-badge--${level}`}
        onClick={() => navigate('/finance')}
        title={`Баланс: ${formatted} ₽`}
      >
        <Wallet size={14} />
      </button>
    );
  }

  return (
    <button
      className={`balance-badge balance-badge--${level}`}
      onClick={() => navigate('/finance')}
      title="Перейти в Финансы"
    >
      <Wallet size={14} />
      <span className="balance-badge__amount">{formatted} ₽</span>
    </button>
  );
}

export default BalanceBadge;
